import { observer } from 'mobx-react-lite';
import { WaitingApprovalLottie } from './WaitingApprovalLottie';
import waitingLottieUrl from './lottie/waiting.lottie';
import rejectedLottieUrl from './lottie/rejected.lottie';
import { WaitingApprovalControls } from './parts/WaitingApprovalControls';
import { WaitingApprovalLocalVideo } from './parts/WaitingApprovalLocalVideo';
import { WaitingApprovalRejectActions } from './parts/WaitingApprovalRejectActions';
import type { WaitingApprovalViewModel } from './useWaitingApprovalViewModel';
import {
    ApprovalCrossfadeLayer,
    ApprovalFooter,
    ApprovalFooterActions,
    ApprovalFooterActionsSlot,
    ApprovalFooterMessageSlot,
    ApprovalFooterText,
    ApprovalFooterTextRow,
    ApprovalMediaArea,
    ApprovalMediaInner,
    ApprovalSpinnerIcon,
    ApprovalStateContainer,
    ApprovalWaitingControls,
    StickerLottieWrapper,
} from './view';

const ApprovalSticker = ({ src }: { src: string }) => (
    <StickerLottieWrapper>
        <WaitingApprovalLottie src={src} />
    </StickerLottieWrapper>
);

type LottieWaitingApprovalLayoutProps = {
    viewModel: WaitingApprovalViewModel;
};

export const LottieWaitingApprovalState = observer(({ viewModel }: LottieWaitingApprovalLayoutProps) => {
    const { isJoinRejected, layout, localVideoTrack, onRetry, onDismiss, controls, labels } = viewModel;

    return (
        <ApprovalStateContainer>
            <ApprovalMediaArea>
                <ApprovalMediaInner>
                    <ApprovalCrossfadeLayer $active={layout === 'video'}>
                        {localVideoTrack && <WaitingApprovalLocalVideo track={localVideoTrack} variant="lottie" />}
                    </ApprovalCrossfadeLayer>
                    <ApprovalCrossfadeLayer $active={layout === 'waiting_placeholder'}>
                        <ApprovalSticker src={waitingLottieUrl} />
                    </ApprovalCrossfadeLayer>
                    <ApprovalCrossfadeLayer $active={layout === 'rejected_placeholder'}>
                        <ApprovalSticker src={rejectedLottieUrl} />
                    </ApprovalCrossfadeLayer>
                </ApprovalMediaInner>
            </ApprovalMediaArea>
            <ApprovalFooter>
                <ApprovalFooterMessageSlot>
                    <ApprovalCrossfadeLayer $active={!isJoinRejected}>
                        <ApprovalFooterTextRow>
                            <ApprovalSpinnerIcon icon="icSpinner" colorVariant="bright" size={20} />
                            <ApprovalFooterText>{labels.waiting}</ApprovalFooterText>
                        </ApprovalFooterTextRow>
                    </ApprovalCrossfadeLayer>
                    <ApprovalCrossfadeLayer $active={isJoinRejected}>
                        <ApprovalFooterText>{labels.declined}</ApprovalFooterText>
                    </ApprovalCrossfadeLayer>
                </ApprovalFooterMessageSlot>
                <ApprovalFooterActionsSlot>
                    <ApprovalCrossfadeLayer $active={!isJoinRejected}>
                        <ApprovalWaitingControls>
                            <WaitingApprovalControls {...controls} onDismiss={onDismiss} />
                        </ApprovalWaitingControls>
                    </ApprovalCrossfadeLayer>
                    <ApprovalCrossfadeLayer $active={isJoinRejected}>
                        <ApprovalFooterActions>
                            <WaitingApprovalRejectActions
                                variant="lottie"
                                retryLabel={labels.retry}
                                dismissLabel={labels.dismiss}
                                onRetry={onRetry}
                                onDismiss={onDismiss}
                            />
                        </ApprovalFooterActions>
                    </ApprovalCrossfadeLayer>
                </ApprovalFooterActionsSlot>
            </ApprovalFooter>
        </ApprovalStateContainer>
    );
});
